import type { QueryRef } from '@apollo/client/react';
import { GetGrainBinStoragePeriodQuery } from '../../api/graphql/__generated';

/**
 * A single storage period as returned by the GraphQL query.
 */
export type StoragePeriodItem = GetGrainBinStoragePeriodQuery['grain_bin_storage_periods'][number];

/**
 * Data returned by the grain bin route loader.
 * Always returns a definite value (never undefined), eliminating the
 * three-state ambiguity of the old ActiveStoragePeriodContext.
 */
export interface GrainBinLoaderData {
  activeStoragePeriod: {
    grain_bin_storage_cycle_id: string;
    start_date: Date;
    end_date: Date | null;
    is_ongoing: boolean;
    storage_cycle_name: string;
    grain_bin_id: string;
    order: number;
  } | null;
  storagePeriods: StoragePeriodItem[];
  grain_bin_id: string | null;
  storagePeriodQueryRef?: QueryRef<GetGrainBinStoragePeriodQuery>;
}

/**
 * Resolves the active (ongoing) storage period from a list of periods.
 *
 * Selection criteria:
 * - `is_ongoing_storage_period` must be true
 * - `start_epoch` must be <= the current time (not a future-scheduled period)
 *
 * Returns the first matching period mapped to the GrainBinLoaderData shape,
 * or null if no period qualifies.
 */
export function resolveActiveStoragePeriod(
  periods: StoragePeriodItem[],
  asOf: Date = new Date()
): GrainBinLoaderData['activeStoragePeriod'] {
  const ongoing = periods.find(
    (storagePeriod) =>
      storagePeriod.is_ongoing_storage_period &&
      new Date(storagePeriod.start_epoch).getTime() <= asOf.getTime()
  );

  if (!ongoing) return null;

  return {
    grain_bin_storage_cycle_id: ongoing.grain_bin_storage_cycle_id,
    start_date: new Date(ongoing.start_epoch),
    end_date: ongoing.end_epoch ? new Date(ongoing.end_epoch) : null,
    is_ongoing: true,
    storage_cycle_name: ongoing.storage_cycle_name,
    grain_bin_id: ongoing.grain_bin_id,
    order: 0,
  };
}
